import { useLocation, useNavigate } from 'react-router-dom';
import { getStateBySlug, getPreferredState, setPreferredState, DEFAULT_STATE, STATES } from '../utils/states';
import { GUIDE_INDEX as ARIZONA_GUIDES } from '../pages/resources/ResourcesHubPage';
import { KANSAS_GUIDES } from '../pages/resources/kansasGuides';

// Guides live at flat URLs (/kansas-ce-faq, /arizona-ce-online…) for SEO, so the URL has
// no state segment. Map each one to its state so the header shows the state the reader is
// actually reading about, not whatever they last picked.
const GUIDE_STATE = {
  ...Object.fromEntries(ARIZONA_GUIDES.map(g => [g.url, 'arizona'])),
  '/kansas-real-estate-ce-requirements': 'kansas',
  ...Object.fromEntries(KANSAS_GUIDES.map(g => [`/${g.slug}`, 'kansas'])),
};
const guideStateFor = pathname => getStateBySlug(GUIDE_STATE[pathname.replace(/\/+$/, '')]);

// Which second-path segments are "translatable" between states (i.e. every state
// gets its own version). If the user switches state while on one of these pages,
// we navigate to the equivalent page in the new state.
const TRANSLATABLE_SUBPATHS = new Set([
  '',              // /arizona → /kansas
  'courses',       // /arizona/courses → /kansas/courses
  'how-to-renew',  // /arizona/how-to-renew → /kansas/how-to-renew
  'resources',     // /arizona/resources → /kansas/resources
]);

// Returns the state derived from the current URL, or the user's preferred
// state if the URL has no state segment.
export function useStateContext() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const firstSeg = segments[0] || '';
  const fromUrl = getStateBySlug(firstSeg);
  if (fromUrl) return { state: fromUrl, source: 'url', urlSubpath: segments.slice(1).join('/') };
  const fromGuide = guideStateFor(location.pathname);
  if (fromGuide) return { state: fromGuide, source: 'guide', urlSubpath: null };
  return { state: getPreferredState(), source: 'preferred', urlSubpath: null };
}

// Returns a function that switches the user to a new state, updating both
// localStorage and (if we're on a translatable page) the URL.
export function useStateNavigator() {
  const location = useLocation();
  const navigate = useNavigate();
  return (newState) => {
    if (!newState) return;
    setPreferredState(newState);

    // Are we on a state-prefixed URL right now?
    const segments = location.pathname.split('/').filter(Boolean);
    const firstSeg = segments[0] || '';
    const currentStateFromUrl = getStateBySlug(firstSeg);

    if (currentStateFromUrl) {
      // On a state-prefixed URL — swap the state prefix
      const rest = segments.slice(1).join('/');
      const subpath = rest.split('/')[0] || '';
      if (TRANSLATABLE_SUBPATHS.has(subpath)) {
        // Preserve the rest of the URL under the new state
        navigate(`/${newState.slug}${rest ? '/' + rest : ''}`);
        return;
      }
      // Not translatable — go to the new state's home
      navigate(`/${newState.slug}`);
      return;
    }

    // On a state's guide: the page can't switch states, so go to the new state's guides.
    const guideState = guideStateFor(location.pathname);
    if (guideState) {
      if (guideState.slug !== newState.slug) navigate(`/${newState.slug}/resources`);
      return;
    }

    // Not on a state URL. If we're on a translatable path like /courses or /how-to-renew,
    // send them to the new state's version of it. Otherwise just save the preference.
    const firstNonEmpty = segments[0] || '';
    if (TRANSLATABLE_SUBPATHS.has(firstNonEmpty)) {
      const rest = segments.slice(1).join('/');
      navigate(`/${newState.slug}${firstNonEmpty ? '/' + firstNonEmpty : ''}${rest ? '/' + rest : ''}`);
    }
    // else: just persist the preference silently — the current page isn't state-scoped
  };
}

export { STATES, DEFAULT_STATE };
